/**
 * Liveness derivation for the poll-fed build-watch status row (#2347/#3025).
 *
 * The autonomous cloud build self-drives its turns on the pod, so the SPA holds
 * no attached agent-activity stream — the push-fed activity snapshot is absent
 * even while the agent is hard at work. That is why the status row used to lie
 * "Build running - no live agents". The truthful signal is `lastActivityAt`:
 * an ABSOLUTE, monotonic epoch-ms stamp the runtime advances on every streamed
 * chunk (including heartbeats), surfaced on the polled
 * `build-game.interview-status@1` response (`status.lastActivityAt`) and the
 * `phase-progress` derived update (`derived.lastActivityAt`).
 *
 * Liveness is TWO-TIER because the stamp only advances while a turn is alive
 * (the runtime's `mirrorAgentChunk` choke): between autonomous turns there are
 * no chunks, so the stamp legitimately holds for a while. Field capture
 * (`.scratch/pp-pause/ws-frames.jsonl`, 654s live build) measured:
 *   - active-operation advances every ~5s (median), with the longest NORMAL
 *     between-advance hold at 26.8s (one turn boundary);
 *   - a deliberate pause pushed the stamp ~2245s stale.
 * So a genuine stall (minutes) is cleanly separable from a turn boundary (tens
 * of seconds). The two windows encode that:
 *   - `<= FRESH` ⇒ `fresh`   (actively streaming right now)
 *   - `<= STALE` ⇒ `quiet`   (alive but between turns — never claim stalled)
 *   - `>  STALE` ⇒ `stalled` (genuinely quiet past any normal turn boundary)
 *   - absent     ⇒ `unknown` (no stamp — older pod image / pre-first-turn)
 */

/**
 * Fresh window — `<=` this since the last activity reads as actively streaming.
 * 3x the 5s interview-status poll cadence (`INTERVIEW_STATUS_LIVE_POLL_MS`),
 * comfortably above the #3025 `>= 2x` floor. The `>= 2x` coupling is pinned by
 * `__tests__/activity-freshness.test.ts`.
 */
export const ACTIVITY_FRESH_WINDOW_MS = 15_000;

/**
 * Stale window — only `>` this reads as a genuine stall. 4x the poll cadence
 * (60s): it clears the field-measured 26.8s max turn-boundary hold with ~2.2x
 * margin (so a legitimate between-turns gap never flashes "stalled"), while
 * sitting far below the ~2245s a real pause produced (so a real stall still
 * surfaces). The 26.8s clearance is pinned by the test.
 */
export const ACTIVITY_STALE_WINDOW_MS = 60_000;

/** Build-liveness inferred from `lastActivityAt`. See module header. */
export type ActivityLiveness = 'fresh' | 'quiet' | 'stalled' | 'unknown';

/**
 * The freshest of two optional activity stamps. interview-status and
 * phase-progress carry the same signal from the same runtime stamp, but a
 * given surface may only have one to hand — take whichever is newer. Returns
 * `null` when neither is present.
 */
export function latestActivityAt(
  a: number | null | undefined,
  b: number | null | undefined,
): number | null {
  const present = [a, b].filter((value): value is number => typeof value === 'number');
  return present.length === 0 ? null : Math.max(...present);
}

/**
 * Classify build liveness from the last recorded agent activity. `nowMs` is
 * injected (never read from the clock in here) so the derivation stays pure and
 * unit-testable; callers pass `Date.now()`.
 *
 * CRITICAL (rider 1 / back-compat): an ABSENT stamp is `unknown`, NEVER
 * `stalled`. `lastActivityAt` is turn-gated and only exists on pods running the
 * PR-A runtime (>= 8bf6fb3); a healthy build on an older image emits none, and
 * calling that "stalled" would just swap one lie for another. `stalled`
 * requires the stamp to have been PRESENT and aged past `ACTIVITY_STALE_WINDOW_MS`.
 */
export function deriveActivityLiveness(
  lastActivityAt: number | null | undefined,
  nowMs: number,
): ActivityLiveness {
  if (typeof lastActivityAt !== 'number') return 'unknown';
  const age = nowMs - lastActivityAt;
  if (age <= ACTIVITY_FRESH_WINDOW_MS) return 'fresh';
  if (age <= ACTIVITY_STALE_WINDOW_MS) return 'quiet';
  return 'stalled';
}

/**
 * Per-game, client-relative liveness memo (#3033).
 *
 * The single build-watch liveness source (`useActivityLiveness`) keeps ONE of
 * these and consumes it everywhere (chat status row, right-panel now-summary,
 * run-state escalations), replacing the three per-consumer
 * `deriveActivityLiveness(stamp, itsOwnNow)` calls (#3034).
 *
 * Why client-relative: `lastActivityAt` is a POD-stamped absolute epoch-ms, but
 * every consumer's `now` is the CLIENT clock. Subtracting one from the other
 * (`clientNow - podStamp`) is sensitive to client-vs-pod clock skew — a client
 * skewed ahead reads a live build stalled early; skewed behind reads a dead
 * build fresh forever (#3033). Instead we remember WHEN (on the client clock)
 * the pod stamp last ADVANCED, and measure elapsed on the client clock only:
 *   - advance detection compares podStamp to the last podStamp (pod-vs-pod) ⇒
 *     skew-independent;
 *   - freshness = clientNow - clientTimeOfLastChange (client-vs-client) ⇒ skew
 *     cancels out.
 */
export interface ActivityLivenessMemo {
  /** The game this memo tracks; a different game resets it. */
  gameId: string | undefined;
  /**
   * The BUILD EPOCH this memo tracks — the Foundry snapshot's build.buildRunId
   * (per build RUN, stable across phases, changes on a new run). A change means
   * a new build epoch, so proof-of-life from the prior run must NOT carry into a
   * retry/new build (#3033 round-3). `null` when no run id is on the wire
   * (desktop/legacy pods, which also carry no `lastActivityAt`, so liveness is
   * `unknown` and never proven there anyway).
   */
  epochKey: string | null;
  /** The last pod-stamped `lastActivityAt` we saw ADVANCE (pod clock). */
  lastSeenLastActivityAt: number | null;
  /** The CLIENT clock time at which that advance was observed (client clock). */
  clientTimeOfLastChange: number | null;
  /**
   * True once a client-OBSERVED advance has been recorded — the stamp advancing
   * past a value we had ALREADY seen, which is POSITIVE proof the pod is alive
   * RIGHT NOW. This is deliberately distinct from the FIRST sighting: a
   * first-seen stamp could be an OLD pod value (a build that stalled before the
   * client mounted), and the client cannot tell without the pod clock — so
   * first-seen is WARM-UP, not proof (#3033 round-2). Warm-up may read `fresh`
   * for DISPLAY, but it must NOT suppress the escalation backstops.
   */
  hasObservedAdvance: boolean;
}

/** A fresh memo with nothing observed yet (classifies as `unknown`). */
export function initActivityLivenessMemo(
  gameId?: string | undefined,
  epochKey: string | null = null,
): ActivityLivenessMemo {
  return {
    gameId,
    epochKey,
    lastSeenLastActivityAt: null,
    clientTimeOfLastChange: null,
    hasObservedAdvance: false,
  };
}

/**
 * Fold a new observation into the client-relative memo. Pure — the caller
 * supplies `clientNowMs` (`Date.now()` at observation time) so the fold stays
 * unit-testable under a simulated skewed clock.
 *
 * Records `clientTimeOfLastChange = clientNowMs` when the pod stamp advances
 * past the last-seen stamp (the first sighting also anchors the change-time). A
 * duplicate or regressing stamp is ignored, so a stuck pod re-sending the same
 * stamp ages into `stalled` rather than reading fresh forever.
 *
 * Epoch scoping (#3033 round-3): a change of `gameId` OR `epochKey` (build run)
 * resets the memo, so proof-of-life from a PRIOR build epoch never carries into
 * a retry/new build — and the new epoch's first stamp is a first-sighting
 * (warm-up), never mistaken for an advance past the previous run's value.
 *
 * UNKNOWN epoch (#3033 round-4): `epochKey === null` means no `buildRunId` is on
 * the wire. `buildRunId` rides the Foundry snapshot channel, INDEPENDENT of the
 * interview-status/phase-progress channels that carry `lastActivityAt`, so a
 * transient desync can deliver stamps while the epoch is unknown. We cannot
 * attribute such a stamp to a known build run, so an unknown epoch can NEVER
 * prove life: `hasObservedAdvance` is never set (and never retained) while
 * `epochKey === null`. Display still tracks the change-time (optimistic), but
 * `provenLive` stays false — the same fail-safe-to-backstops stance as warm-up.
 *
 * Absent stamp: CLEARS any stale proof back to warm-up (rather than leaving the
 * memo untouched). A retry whose first poll carries no stamp yet — or any epoch
 * that regresses to no stamp — must start `provenLive === false`, so an absent
 * `lastActivityAt` can never keep a prior epoch's `hasObservedAdvance` alive.
 * Classification stays `unknown` (tri-state) while no stamp is present.
 *
 * `hasObservedAdvance` flips true only on an advance past an ALREADY-seen value
 * within the CURRENT KNOWN epoch (not the first sighting, never in an unknown
 * epoch): that is the POSITIVE proof-of-life signal, distinct from warm-up. Once
 * set it stays set until the epoch (or game) changes or the stamp goes absent.
 */
export function observeActivity(
  memo: ActivityLivenessMemo,
  observation: {
    gameId: string | undefined;
    epochKey?: string | null;
    lastActivityAt: number | null | undefined;
    clientNowMs: number;
  },
): ActivityLivenessMemo {
  const epochKey = observation.epochKey ?? null;
  const sameEpoch = observation.gameId === memo.gameId && epochKey === memo.epochKey;
  const base = sameEpoch ? memo : initActivityLivenessMemo(observation.gameId, epochKey);
  const stamp = observation.lastActivityAt;
  if (typeof stamp !== 'number') {
    // Absent stamp ⇒ this epoch has no recorded activity; it must not carry
    // stale proof. Reset to warm-up unless already there (keep identity stable).
    if (base.lastSeenLastActivityAt === null && !base.hasObservedAdvance) return base;
    return initActivityLivenessMemo(base.gameId, base.epochKey);
  }
  const lastSeen = base.lastSeenLastActivityAt;
  const advanced = lastSeen === null || stamp > lastSeen;
  if (!advanced) return base;
  // An UNKNOWN epoch (epochKey === null) can never prove life: track the
  // change-time for the optimistic DISPLAY verdict, but refuse to set or retain
  // hasObservedAdvance until a real build-run id is present (#3033 round-4).
  const knownEpoch = base.epochKey !== null;
  return {
    gameId: base.gameId,
    epochKey: base.epochKey,
    lastSeenLastActivityAt: stamp,
    clientTimeOfLastChange: observation.clientNowMs,
    // A first sighting (lastSeen === null) is warm-up: the stamp could be an old
    // pod value, so it is NOT proof. Only an advance past an already-seen value
    // (lastSeen !== null) within a KNOWN epoch is a client-observed advance ⇒
    // proof of life.
    hasObservedAdvance: knownEpoch && (base.hasObservedAdvance || lastSeen !== null),
  };
}

/**
 * Classify liveness from the client-relative memo. Reuses the SAME threshold
 * classifier (`deriveActivityLiveness`) — fed the client-observed change-time
 * instead of the pod stamp — so the 15s/60s windows and the tri-state `unknown`
 * (never-seen stamp) are shared, and their tests stay authoritative.
 *
 * NOTE: this is the DISPLAY verdict; it reads `fresh` during warm-up. For the
 * escalation gate use `livenessProvesLife`, which additionally requires a
 * client-observed advance.
 */
export function classifyClientRelativeLiveness(
  memo: ActivityLivenessMemo,
  clientNowMs: number,
): ActivityLiveness {
  return deriveActivityLiveness(memo.clientTimeOfLastChange, clientNowMs);
}

/**
 * Does the memo constitute POSITIVE proof the build is alive RIGHT NOW — i.e.
 * is it safe to SUPPRESS the stale-build / kernel-final-review escalations?
 *
 * True ONLY when (a) a client-observed advance has been recorded
 * (`hasObservedAdvance`) AND (b) the current client-relative classification is
 * `fresh` or `quiet`. A WARM-UP `fresh` (first-seen stamp, no advance observed
 * yet) is NOT proof — it could be an old pod stamp from a build that stalled
 * before the client mounted, so it must NOT suppress the backstops; those fire
 * on their OWN criteria (the 15-minute wall clock, the kernel idle-heartbeat).
 * `stalled`/`unknown` are never proof. This preserves skew-immunity: once an
 * advance is observed, the measurement is entirely client-relative (#3033).
 */
export function livenessProvesLife(memo: ActivityLivenessMemo, clientNowMs: number): boolean {
  if (!memo.hasObservedAdvance) return false;
  const liveness = classifyClientRelativeLiveness(memo, clientNowMs);
  return liveness === 'fresh' || liveness === 'quiet';
}

/**
 * Client-relative phase-start memo (#3035).
 *
 * The Foundry snapshot exposes NO phase-start timestamp — only
 * `phase.{current,label,subPhase,progress,blockedBy}`. On the snapshot-only
 * degraded path (phase-progress subscription undefined) the run-state stale-build
 * detector still needs a phase-start anchor for its 15-minute window. Rather than
 * add a runtime-side snapshot field (cross-repo, version-bump cascade), we reuse
 * the SAME client-relative pattern as `ActivityLivenessMemo`: remember WHEN (on
 * the CLIENT clock) the snapshot `phase.current` was first observed, per build
 * epoch, and let the detector age it against the client escalation clock. Both
 * ends are the client clock, so the elapsed measurement is skew-immune.
 *
 * Semantics mirror the liveness memo: a change of `gameId` OR `epochKey` (build
 * run) resets the anchor so a retry/new run does not inherit the prior run's
 * phase-start; a change of `phase.current` re-anchors (each phase gets its own
 * window budget); an absent phase clears the anchor (fail-safe — a young/absent
 * phase never escalates).
 *
 * UNKNOWN epoch (#3035 round-2/round-3, mirrors #3098 round-4): `epochKey === null`
 * means no `buildRunId` is on the wire (legacy / version-skewed pod). A null epoch
 * is NOT a stable run identity, so a build that resumes/retries into the same
 * phase under a still-null epoch must not be allowed to escalate off an inherited
 * cross-run anchor. That fail-safe now lives in the DETECTOR
 * (`staleBuildAttentionFromKernel` requires `build.buildRunId`), NOT in this memo:
 * the memo HOLDS a STABLE anchor for ALL epochs so it never re-anchors on every
 * render (round-2 re-anchored a null epoch to `now` each render, which churned the
 * `workspaceRunState` memo that feeds `usePreviewModule` — a real
 * every-render-recompute regression, #3035 round-3). Epoch scoping still resets on
 * a `buildRunId` change, so a KNOWN-epoch retry (run-A → run-B) never inherits the
 * prior run's anchor either.
 */
export interface SnapshotPhaseStartMemo {
  /** The game this memo tracks; a different game resets it. */
  gameId: string | undefined;
  /** The build epoch (Foundry snapshot `build.buildRunId`); a change resets it.
   *  `null` = unknown epoch (the detector refuses to escalate under it). */
  epochKey: string | null;
  /** The snapshot `phase.current` currently anchored; a change re-anchors. */
  phaseCurrent: number | null;
  /** CLIENT-clock time when this `phase.current` was first observed (this epoch). */
  clientTimeOfPhaseStart: number | null;
}

/** A fresh phase-start memo with nothing anchored yet. */
export function initSnapshotPhaseStartMemo(
  gameId?: string | undefined,
  epochKey: string | null = null,
): SnapshotPhaseStartMemo {
  return { gameId, epochKey, phaseCurrent: null, clientTimeOfPhaseStart: null };
}

/**
 * Fold a snapshot phase observation into the client-relative phase-start memo.
 * Pure — the caller supplies `clientNowMs` (`Date.now()` at observation time) so
 * the fold stays unit-testable.
 *
 * Anchors `clientTimeOfPhaseStart = clientNowMs` on the FIRST sighting of a
 * `phase.current` value within the current game+epoch, and re-anchors whenever
 * `phase.current` changes. An absent phase (`null`/`undefined`) clears the anchor
 * back to un-anchored, so a snapshot that momentarily drops the phase restarts
 * the window rather than escalating on a stale anchor (fail-safe). A game or
 * epoch change resets the memo, so a retry/new build never inherits the prior
 * run's phase-start.
 *
 * Stable HOLD (#3035 round-3): the anchor is held across same-phase observations
 * for ALL epochs, so the returned value only changes on a real phase / game /
 * epoch transition — NEVER every render. (Round-2 re-anchored a null epoch to
 * `now` each render as its unknown-epoch fail-safe; that churned the
 * `workspaceRunState` memo — a live `usePreviewModule` input — on every 1s
 * kickoff-clock tick, a real perf/timing regression.) The unknown-epoch fail-safe
 * moved to the DETECTOR (`staleBuildAttentionFromKernel` refuses to escalate
 * without a `buildRunId`), which delivers the same guarantee with no churn.
 */
export function observeSnapshotPhaseStart(
  memo: SnapshotPhaseStartMemo,
  observation: {
    gameId: string | undefined;
    epochKey?: string | null;
    phaseCurrent: number | null | undefined;
    clientNowMs: number;
  },
): SnapshotPhaseStartMemo {
  const epochKey = observation.epochKey ?? null;
  const sameEpoch = observation.gameId === memo.gameId && epochKey === memo.epochKey;
  const base = sameEpoch ? memo : initSnapshotPhaseStartMemo(observation.gameId, epochKey);
  const phaseCurrent =
    typeof observation.phaseCurrent === 'number' ? observation.phaseCurrent : null;
  if (phaseCurrent === null) {
    // No phase on the wire ⇒ nothing to anchor. Reset unless already cleared
    // (keep identity stable so the memo ref does not churn every render).
    if (base.phaseCurrent === null && base.clientTimeOfPhaseStart === null) return base;
    return initSnapshotPhaseStartMemo(base.gameId, base.epochKey);
  }
  // HOLD the anchor across same-phase observations (stable — no per-render churn).
  // This ages the window so a genuinely-stuck build surfaces. Epoch scoping above
  // already re-anchors on a game/epoch change, so a retry never inherits a stale
  // anchor; the unknown-epoch fail-safe is the detector's buildRunId guard.
  if (base.phaseCurrent === phaseCurrent && base.clientTimeOfPhaseStart !== null) {
    return base;
  }
  // First sighting of this phase within the current game+epoch ⇒ anchor the time.
  return {
    gameId: base.gameId,
    epochKey: base.epochKey,
    phaseCurrent,
    clientTimeOfPhaseStart: observation.clientNowMs,
  };
}
